import Wallet, {
  BitcoinNetworkType,
  RpcErrorCode,
  SignMultipleTransactionOptions,
  signMultipleTransactions,
  SignMultipleTransactionsResponse,
} from 'sats-connect';

import { Buff } from '@cmdcode/buff';
import { hash160, PSBT, taptweak_pubkey, TX } from '@ducat-unit/client-sdk/util';
import { SignPSBTEntry, Transaction, WalletAccountRecord, WalletConnectAPI } from '@ducat-unit/client-sdk';
import { request, AddressPurpose } from '@sats-connect/core';
import { VaultWallet, OracleAPI } from '@ducat-unit/client-sdk';
import { Logger } from '@/constants';
import { WalletInfo } from '@/context/types';
import { NetworkEnum } from '@/config/config.types';
import { ScriptData } from '@scrow/tapscript';

import { hexToBase64 } from './util';

const logger = Logger('lib/connector/xverse');

// async function get_wallet_accounts_old(): Promise<WalletAccountRecord> {
//   const response = await Wallet.request("getAccounts", {
//     purposes: [AddressPurpose.Payment, AddressPurpose.Ordinals],
//   });
//   if (response.status === "error") throw response.error;
//   const sats_acct = response.result.find(
//     (addr) => addr.purpose === AddressPurpose.Payment
//   );
//   if (sats_acct === undefined) {
//     throw new Error("Sats address not defined for the wallet.");
//   }
//   const rune_acct = response.result.find(
//     (addr) => addr.purpose === AddressPurpose.Ordinals
//   );
//   if (rune_acct === undefined) {
//     throw new Error("Rune address not defined for the wallet");
//   }
//   return {
//     sats: { address: sats_acct.address, pubkey: sats_acct.publicKey },
//     runes: { address: rune_acct.address, pubkey: rune_acct.publicKey },
//     vault: { address: rune_acct.address, pubkey: rune_acct.publicKey },
//   };
// }

type AdressResultXverse = {
  address: string;
  publicKey: string;
  purpose: AddressPurpose;
};

function computeAccountRecord_xverse(addresses: AdressResultXverse[]): WalletAccountRecord {
  const sats_acct = addresses.find((addr) => addr.purpose === AddressPurpose.Payment);
  if (sats_acct === undefined) {
    throw new Error('Sats address not defined for the wallet.');
  }
  const rune_acct = addresses?.find((addr) => addr.purpose === AddressPurpose.Ordinals);
  if (rune_acct === undefined) {
    throw new Error('Rune address not defined for the wallet');
  }
  return {
    sats: { address: sats_acct.address, pubkey: sats_acct.publicKey },
    runes: { address: rune_acct.address, pubkey: rune_acct.publicKey },
    vault: { address: rune_acct.address, pubkey: rune_acct.publicKey },
  };
}

async function get_wallet_accounts(): Promise<WalletAccountRecord> {
  const response = await request('wallet_connect', {
    addresses: [AddressPurpose.Payment, AddressPurpose.Ordinals],
  });

  if (response.status === 'error') {
    throw response.error;
  }

  return computeAccountRecord_xverse(response.result.addresses);
}

// await request('getInfo', null).then(res => console.log(`getInfo`, res)); // {result: {metods: [<string>], version}}
// await request('wallet_getWalletType', null).then(res => console.log(`wallet_getWalletType`, res)); // {result: "software"}
// await request('wallet_getAccount', null).then(res => console.log(`wallet_getAccount`, res)); // {result: {addresses: [<addr>], id, walletType}}
// await request('getAccounts', { purposes }).then(res => console.log(`getAccounts`, res)); // {result: [<addresses>]} (2), PROMPTS user
// await request('getAddresses', { purposes }).then(res => console.log(`getAddresses`, res)); // {result: {addresses: [<addresses>]}}
// await request('getBalance', null).then(res => console.log(`getBalance`, res)); // {result: {confirmed, total, unconfirmed}} // strings
async function reconnect(walletInfo: WalletInfo) {
  const res = await request('wallet_getCurrentPermissions', null);
  // console.log(`[wallet_getCurrentPermissions]`, res);
  const purposes = [AddressPurpose.Payment, AddressPurpose.Ordinals];

  if (res.status === 'error') {
    throw res.error;
  }

  const resAddresses = await request('getAddresses', { purposes });

  if (resAddresses.status === 'error') {
    throw resAddresses.error;
  }

  const walletAccount = computeAccountRecord_xverse(resAddresses.result.addresses);

  if (walletAccount.sats.address !== walletInfo?.address) {
    throw Error('Expected different wallet address to be connected');
  }

  return walletAccount;
}

const get_balance_api = (client: VaultWallet) => async () => {
  logger.debug('Fetching balance');

  const addr = client.acct.sats.address;
  const host = client.config.indexer.ord;
  const res = await OracleAPI.wallet.fetch_address_bal(host, addr);

  if (!res.ok) {
    throw new Error(res.error);
  }

  logger.debug(res.data);

  return res.data;
};

const get_sats_utxos_api = (client: VaultWallet) => async () => {
  logger.debug('fetching sats utxos');
  const addr = client.acct.sats.address;
  const script = TX.parse_address(addr).hex;
  const host = client.config.indexer.esp;
  const res = await OracleAPI.esplora.esplora_get_utxos(host, addr);

  if (!res.ok) {
    throw new Error(res.error);
  }

  const utxos = res.data.map((e) => {
    const { txid, vout, value } = e;

    return { txid, vout, value, script };
  });

  logger.debug(utxos);

  return utxos;
};

const get_rune_utxos_api = (client: VaultWallet) => async () => {
  logger.debug('get rune utxos');

  const addr = client.acct.runes.address;
  const host = client.config.indexer;
  const res = await OracleAPI.wallet.fetch_rune_utxos(host.ord, addr);

  console.log(`>>> [get_rune_utxos_api] res:`, res);

  if (!res.ok) {
    throw new Error(res.error);
  }

  logger.debug(res.data);

  return res.data;
};

const get_vault_tokens_api = (client: VaultWallet) => async () => {
  logger.debug('get vault tokens');

  const address = client.acct.vault.address;
  const esp_host = client.config.indexer.esp;
  const ord_host = client.config.indexer.ord;
  const postage = client.config.postage.vault;

  const res = await OracleAPI.wallet.fetch_vault_tokens(esp_host, ord_host, address, postage);

  if (!res.ok) {
    throw new Error(res.error);
  }

  logger.debug(res.data);

  return res.data;
};

const sign_utxos_api = (client: VaultWallet) => {
  logger.debug('sign utxos');

  const runes_addr = client.acct.runes.address;
  const runes_pk = client.acct.runes.pubkey;
  const runes_tpk = taptweak_pubkey(runes_pk);
  const sats_addr = client.acct.sats.address;
  const sats_pk = client.acct.sats.pubkey;
  const sats_pkh = hash160(sats_pk);

  const sats_meta = TX.parse_address(client.acct.sats.address);
  const manifest: Record<string, number[]> = {
    [runes_addr]: [],
    [sats_addr]: [],
  };
  // Define sign_psbt method.
  const sign_psbt = client.conn.sign.psbt(client);

  // Return wrapped sign_psbt method.
  return async (psbt: string) => {
    /* Pre-Processing the PSBT */

    // Decode the PSBT string.
    let pdata = PSBT.parse(psbt);

    logger.debug('sign utxo pre-process');
    logger.debug(pdata);

    // For each input in the PSBT:
    for (let i = 0; i < pdata.inputsLength; i++) {
      // Fetch the tx input at index (i).
      const txinput = pdata.getInput(i);
      // Fetch the prevout data for the input.
      const prevout = txinput.witnessUtxo;

      // If prevout is undefined, skip.
      if (prevout === undefined) continue;

      // Parse the metadata for the locking script.
      const meta = TX.parse_script_meta(prevout.script as ScriptData);

      // If the script key is undefined, skip.
      if (meta.key === undefined) continue;

      // If the script type is p2sh:
      if (meta.type === 'p2sh' && sats_meta.type === 'p2sh') {
        if (meta.key.hex === sats_meta.key) {
          // fix for xverse wallet using wrapped segwit :-(
          const script = '0014' + sats_pkh;
          // Update the input for P2SH.
          pdata.updateInput(i, {
            redeemScript: Buff.hex(script),
            finalScriptSig: Buff.hex(script),
          });
        }
        manifest[sats_addr].push(i);
      }

      if (meta.type === 'p2w-pkh') {
        if (meta.key.hex === sats_pkh) {
          manifest[sats_addr].push(i);
        }
      }

      if (meta.type === 'p2tr') {
        if (meta.key.hex === runes_tpk) {
          // Update the input for P2TR key spending.
          pdata.updateInput(i, {
            tapInternalKey: Buff.hex(client.acct.runes.pubkey),
          });
          manifest[runes_addr].push(i);
        }
      }
    }

    /* Soliciting the Signing Device */

    psbt = PSBT.encode(pdata);
    psbt = await sign_psbt(psbt, manifest);

    pdata = PSBT.decode(psbt);

    logger.debug('sign utxo post-process');
    logger.debug(pdata);

    /* Post-Processing the PSBT */

    const indexes = Object.values(manifest).reduce((p, n) => [...p, ...n], [] as number[]);

    indexes.forEach((idx) => {
      // Define an array for witness elements.
      const witness: Uint8Array[] = [];
      // Get the current input.
      const pvin = pdata.getInput(idx);
      // Define the current redeem script, if exists.
      const script = pvin.redeemScript;
      // Define the current signature, if exists.
      const psig = pvin.partialSig?.at(0);
      // Define the current scriptSig, if exists.
      const scriptSig = pvin.finalScriptSig;

      // If a partial signature exists:
      if (psig !== undefined) {
        // Push it to the witness stack.
        witness.push(...psig.reverse());
      }

      // If a redeem script exists:
      if (script !== undefined) {
        // If scriptSig is undefined:
        if (scriptSig === undefined) {
          // Push it to the witness stack.
          witness.push(script);
        }
      }

      // If the witness stack is not empty:
      if (witness.length > 0) {
        // Finalize the witness for the input.
        pdata.updateInput(idx, { finalScriptWitness: witness });
      }
    });

    logger.debug('psbt finalized');
    logger.debug(pdata);

    // Return the encoded PSBT.
    return PSBT.encode(pdata);
  };
};

const sign_psbt_api =
  (client: VaultWallet) =>
  async (psbt: string, signInputs: Record<string, number[]>): Promise<string> => {
    const pre_pdata = PSBT.decode(psbt);

    logger.debug('sign psbt pre-process');
    logger.debug(pre_pdata);

    psbt_pre_process(client, pre_pdata, signInputs);

    logger.debug('sign psbt pre-process complete');
    logger.debug(pre_pdata);

    const res = await request('signPsbt', {
      psbt: PSBT.encode(pre_pdata),
      signInputs,
      broadcast: false,
    });

    if (res.status === 'error') {
      throw res.error;
    }

    // Note: some wallets return psbt in base64 (Xverse) and some in hex (Unisat):
    const psbtBase64 = res.result.psbt.startsWith('70736274') ? hexToBase64(res.result.psbt) : res.result.psbt;
    const post_pdata = PSBT.decode(psbtBase64);

    logger.debug('sign psbt post-process');
    logger.debug(post_pdata);

    psbt_post_process(client, post_pdata, signInputs);

    logger.debug('sign psbt post-process complete');
    logger.debug(post_pdata);

    return PSBT.encode(post_pdata);
  };

export function psbt_pre_process(client: VaultWallet, pdata: Transaction, manifest: Record<string, number[]>, isSwap?: boolean) {
  const sats_pkh = hash160(client.acct.sats.pubkey);
  const runes_tpk = taptweak_pubkey(client.acct.runes.pubkey);

  for (const [address, inputs] of Object.entries(manifest)) {
    const addr_meta = TX.parse_address(address);
    for (const idx of inputs) {
      // Fetch the tx input at index (i).
      const txinput = pdata.getInput(idx);
      // Fetch the prevout data for the input.
      const prevout = txinput.witnessUtxo;

      // If prevout is undefined, skip.
      if (prevout === undefined) continue;

      // Parse the metadata for the locking script.
      const script_meta = TX.parse_script_meta(prevout.script as ScriptData);
      const script_type = script_meta.type;
      const script_key = script_meta.key?.hex;

      // If the script key is undefined, skip.
      if (script_key === undefined) continue;

      // If the script type is p2sh:
      if (script_type === 'p2sh' && script_key === addr_meta.key) {
        // fix for xverse wallet using wrapped segwit :-(
        const script = '0014' + sats_pkh;

        // Only update if redeemScript is missing
        if (!txinput.redeemScript) {
          try {
            if (isSwap) {
              // Swap flow: only add redeemScript
              pdata.updateInput(idx, {
                redeemScript: Buff.hex(script),
              });
            } else {
              // Liquidation flow (default): add both redeemScript and finalScriptSig
              pdata.updateInput(idx, {
                redeemScript: Buff.hex(script),
                finalScriptSig: Buff.hex(script),
              });
            }
          } catch (error: unknown) {
            const errorMessage = error instanceof Error
              ? error.message
              : (String(error) === '[object Object]')
                ? JSON.stringify(error)
                : String(error);

            if (errorMessage.includes('duplicate')) {
              // Ignore duplicate errors, but log them
              console.log('Ignoring duplicate data for input', idx);
            } else {
              throw error;
            }
          }
        }
      }

      if (script_type === 'p2w-pkh' && script_key === sats_pkh) {
        // Pre-processing for pay-to-witness-pubkey-hash.
      }

      if (script_type === 'p2tr') {
        if (script_key === runes_tpk) {
          // Update the input for P2TR key spending.
          pdata.updateInput(idx, {
            tapInternalKey: Buff.hex(client.acct.runes.pubkey),
          });
        }
      }
    }
  }
}

export function psbt_post_process(client: VaultWallet, pdata: Transaction, manifest: Record<string, number[]>) {
  const vault_pk = client.acct.vault.pubkey;

  for (const [_, inputs] of Object.entries(manifest)) {
    for (const idx of inputs) {
      // Fetch the tx input at index (i).
      const txinput = pdata.getInput(idx);
      // Fetch the prevout data for the input.
      const prevout = txinput.witnessUtxo;

      // If prevout is undefined, skip.
      if (prevout === undefined) continue;

      // Define the current redeem script, if exists.
      const redeem_script = txinput.redeemScript;
      // Define the current signature, if exists.
      const segwit_sig = txinput.partialSig?.at(0);
      // Define the current scriptSig, if exists.
      const scriptSig = txinput.finalScriptSig;
      // Define the current taproot key-spend, if exists.
      const tapkey_sig = txinput.tapKeySig;
      // Define the current taproot scriptSig, if exists.
      const tapscript_sig = txinput.tapScriptSig;
      // Define the current witness, if exists.
      const final_witness = txinput.finalScriptWitness;
      // Define an array for witness elements.
      let witness: Uint8Array[] = final_witness ?? [];

      // If a partial signature exists:
      if (segwit_sig !== undefined && witness.length === 0) {
        // Push it to the witness stack.
        witness.push(...segwit_sig.reverse());
      }

      // If a redeem script exists:
      if (redeem_script !== undefined && scriptSig === undefined) {
        // Push it to the witness stack.
        witness.push(redeem_script);
      }

      // If a taproot key-spend has been defined:
      if (tapkey_sig !== undefined && witness.length === 0) {
        // Set the final witness as the signature.
        // Update the psbt input with the final witness.
        witness.push(tapkey_sig);
      }

      if (tapscript_sig !== undefined) {
        const elem = witness?.at(0);

        // If the witness is empty, skip.
        if (elem === undefined) continue;

        // Convert the element to a hex string.
        const elem_hex = new Buff(elem).hex;

        // If the element hex matches the vault pubkey:
        if (elem_hex === vault_pk) {
          // Remove the erroneous pubkey!
          witness = witness.slice(1);
        }
      }

      // If the witness stack is not empty:
      if (witness.length > 0) {
        // Finalize the witness for the input.
        pdata.updateInput(idx, { finalScriptWitness: witness });
      }
    }
  }
}

// https://docs.xverse.app/sats-connect/bitcoin-methods/signmultipletransactions
const sign_batch =
  (client: VaultWallet) =>
  async (psbts: SignPSBTEntry[]): Promise<string[]> => {
    console.log(`[sign_batch] psbts:`, psbts);
    const [psbt1, signInputs1] = psbts[0];
    const pre_pdata1 = PSBT.decode(psbt1);
    psbt_pre_process(client, pre_pdata1, signInputs1);

    const [psbt2, signInputs2] = psbts[1];
    const pre_pdata2 = PSBT.decode(psbt2);
    psbt_pre_process(client, pre_pdata2, signInputs2);

    return new Promise(async (resolve, reject) => {
      const batchOptions: SignMultipleTransactionOptions = {
        payload: {
          network: { type: BitcoinNetworkType.Signet },
          message: 'Sign Ducat Transaction',
          psbts: [
            {
              psbtBase64: PSBT.encode(pre_pdata1),
              inputsToSign: Object.keys(signInputs1).map((key) => ({
                address: key,
                signingIndexes: signInputs1[key],
              })),
            },
            {
              psbtBase64: PSBT.encode(pre_pdata2),
              inputsToSign: Object.keys(signInputs2).map((key) => ({
                address: key,
                signingIndexes: signInputs2[key],
              })),
            },
          ],
        },

        onFinish: (response: SignMultipleTransactionsResponse) => {
          console.log('!!!Bulk tx signing response:', response);
          const post_pdata1 = PSBT.decode(response[0].psbtBase64);
          const post_pdata2 = PSBT.decode(response[1].psbtBase64);

          psbt_post_process(client, post_pdata1, signInputs1);
          psbt_post_process(client, post_pdata2, signInputs2);

          const finalPsbts = [PSBT.encode(post_pdata1), PSBT.encode(post_pdata2)];
          console.log('>>> finalPsbts:', finalPsbts);

          resolve(finalPsbts);
        },
        onCancel: () => reject(new Error('Transaction signing was cancelled by user')),
      };

      console.log(`>>> batch options:`, batchOptions);
      signMultipleTransactions(batchOptions);
    });
  };

function sign_message(payload: { address: string; message: string }) {
  return request('signMessage', payload);
}

function disconnect() {
  return request('wallet_disconnect', null);
}

// For unit swap feature.
async function signPsbt(
  client: VaultWallet,
  psbt: string,
  signInputs: Record<string, number[]>,
  broadcast: boolean,
  isSwap?: boolean,
): Promise<string> {
  const pre_pdata = PSBT.decode(psbt);
  psbt_pre_process(client, pre_pdata, signInputs, isSwap);

  const res = await request('signPsbt', {
    psbt: PSBT.encode(pre_pdata),
    signInputs,
    broadcast: broadcast ?? false,
  });

  if (res.status === 'error') {
    throw res.error;
  }

  // Note: some wallets return psbt in base64 (Xverse) and some in hex (Unisat):
  const psbtBase64 = res.result.psbt.startsWith('70736274') ? hexToBase64(res.result.psbt) : res.result.psbt;

  return broadcast && res.result.txid ? res.result.txid : psbtBase64;
}

// https://docs.xverse.app/sats-connect/bitcoin-methods/sendtransfer *Xverse doesn't support custom fees*
const sendTransfer = async ({
  address,
  amountSats,
  feeRate: _,
}: {
  amountSats: number;
  address: string;
  feeRate?: number;
}): Promise<TransferResult> => {
  try {
    // validate amount - Xverse requires minimum 1500 sats
    if (amountSats < 1500) {
      console.error('sendTransfer: amount too small', { amountSats });

      return { error: true, message: `Amount must be at least 1500 sats, got ${amountSats}` };
    }

    const response = await request('sendTransfer', {
      recipients: [
        {
          address,
          amount: amountSats,
        },
      ],
    });

    // handle success case
    if (response.status === 'success') {
      return { error: false, txid: response.result.txid };
    }

    // handle user rejection (user closed Xverse window or cancelled)
    if (response.error?.code === RpcErrorCode.USER_REJECTION) {
      return { error: true, message: 'Transaction was cancelled by user' };
    }

    // handle other errors
    console.log('sendTransfer: error', response.error);
    const errorMessage = response.error?.message || 'Failed to send transaction';

    return { error: true, message: errorMessage };
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
    console.error('sendTransfer error:', err);

    return { error: true, message: errorMessage };
  }
};

export type FunctionVoid = () => void;

export type WalletIsValidResult = {
  isValid: boolean;
  errorMessage: string;
};

export type TransferResult = {
  error: boolean;
  txid?: string;
  message?: string;
};

export type WalletConnector = {
  api: WalletConnectAPI;

  getWalletAccounts: () => Promise<WalletAccountRecord>;
  reconnect?: (walletInfo: WalletInfo) => void;
  signMessage: (payload: { address: string; message: string }) => Promise<any>; // FIXME: any type
  sendTransfer: (payload: { address: string; amountSats: number; feeRate?: number }) => Promise<TransferResult>;
  signPsbt?: (
    client: VaultWallet,
    psbt: string,
    signInputs: Record<string, number[]>,
    broadcast: boolean,
    isSwap?: boolean,
  ) => Promise<string>;
  disconnect: () => void;
  switchNetwork: (network: NetworkEnum) => void;
  subscribe: {
    accountChange: (cb: FunctionVoid) => FunctionVoid;
    networkChange: (cb: FunctionVoid) => FunctionVoid;
  };
  isValidWalletAccountRecord: (accountRecord: WalletAccountRecord) => WalletIsValidResult;
};

const subscribe = (event: 'accountChange' | 'networkChange') => (onChange: FunctionVoid) => {
  return Wallet.addListener(event, onChange);
};

// Default connector (original Xverse).
export const WALLET_CONNECTOR: WalletConnector = {
  api: {
    fetch: {
      balance: get_balance_api,
      sats_utxos: get_sats_utxos_api,
      rune_utxos: get_rune_utxos_api,
      vault_tokens: get_vault_tokens_api,
    },

    sign: {
      psbt: sign_psbt_api,
      utxos: sign_utxos_api,
      batch: sign_batch,
    },
  },

  // Extra methods related to specific wallet (not related to client-sdk)
  getWalletAccounts: get_wallet_accounts,
  reconnect,
  signMessage: sign_message,
  signPsbt,
  sendTransfer,
  disconnect,
  switchNetwork: () => false,
  subscribe: {
    accountChange: subscribe('accountChange'),
    networkChange: subscribe('networkChange'),
  },
  isValidWalletAccountRecord: () => ({
    isValid: true,
    errorMessage: '',
  }),
};
